import React, { useEffect, useState, useMemo } from 'react';
import { useIntl } from 'umi';
import CapsuleBar from '@/pages/strategic/components/CapsuleBar';
import { TMode } from '@/pages/strategic/components/CapsuleBar/type.d';
import { IBusinessGrossRevenueCapsule } from "../type.d";
import CustomCollapse from '@sg/components/Collapse';
import { STORAGE_KEYS } from '@sg/utils/sessionStorage';
import DefinitionBubble from '@sg/components/DefinitionBubble';
import { SGBuriedBtnPoint, createBtnPoint, ExecutiveSummaryTab } from '@sg/utils/buriedPoint';

interface ICapsultBoardProps {
  data: IBusinessGrossRevenueCapsule;
  coefficient?: number;
  capsuleMode?: TMode;
  type: 'parent' | 'child';
  capsuleHeight?: number;
  maxValue?: number;
  // 同步组的操作
  syncGroupChange?: (data: IBusinessGrossRevenueCapsule, ids: string[]) => void;
  // 发射端和接收端
  mode?: 'send' | 'receive';
  // 同步的id
  syncIds?: Record<string, string[]>;
  valueFormatter?: (value: number) => string;
}

export default function CapsuleBoard(props: ICapsultBoardProps) {
  const { data, coefficient = 1, capsuleMode = 'dual', type = 'parent', capsuleHeight = 32, maxValue, syncGroupChange, mode, syncIds, valueFormatter } = props;
  const { title, metrics, isDefaultFold, directionType, isOpacity } = data;
  const [activeIds, setActiveIds] = useState<string[]>([]);
  const { formatMessage } = useIntl();

  const handleStateChange = (ids: string[]) => {
    const previousActiveIds = activeIds;
    setActiveIds(ids);

    // Track buried point for Capabilities & Functions collapse
    // Only track if this is the "Capabilities & Functions" collapse (title is 'dbs_center_capability')
    // and it's on the Executive Summary page (pageId: 100011)
    if (title === 'dbs_center_capability' && isDefaultFold) {
      const isExpanded = ids.includes('1');
      const wasExpanded = previousActiveIds.includes('1');

      // Only track when state actually changes (expanded to collapsed or vice versa)
      if (isExpanded !== wasExpanded) {
        const btnPoint = createBtnPoint(
          ExecutiveSummaryTab,
          'capabilitiesFunctionsCollapse',
          `Capabilities & Functions - ${isExpanded ? 'Expanded' : 'Collapsed'}`
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      }
    }

    if (mode === 'send') {
      syncGroupChange?.(data, ids);
    }
  };

  const getPaddingBottom = () => {
    // 测试环境activeIds打开时[0,1]，关闭是[0]预发布打开是[1]，关闭是[]
    // Check if this is the last child and if collapse is active
    if (isDefaultFold && !activeIds.includes('1')) {
      return '0px'; // Active state
    }
    if (capsuleMode === 'single') {
      return '20px';
    }
    return '40px';
  };

  useEffect(() => {
    if (mode === 'receive') {
      Object.keys(syncIds || {}).forEach(key => {
        if (key === data.group) {
          setActiveIds(syncIds?.[key] || []);
        }
      });
    }
  }, [syncIds]);

  const titleContent = useMemo(() => {
    if (title) {
      if (title === 'dbs_center_capability' && isDefaultFold && !isOpacity) {
        return <DefinitionBubble
          definition={'Consolidated unallocated cost of Tech, OGS, Branding, SPM, OIT, Midas, BOS, Venture Lab'}
          dark
          placement="top"
          arrowPointAtCenter
          style={{ minWidth: '450px' }}
        >
          {formatMessage({ id: title })}
        </DefinitionBubble>;
      }
      return formatMessage({ id: title });
    }
    return null;
  }, [title, formatMessage]);

  return (
    <div
      className={type === 'child' ? 'dbs-home-metric-card-capsule-child-wrap' : 'dbs-home-metric-card-capsule-parent-wrap'}
      style={{ paddingBottom: getPaddingBottom(), opacity: isOpacity ? '0' : '1' }}
    >
      {
        isDefaultFold
          ? <CustomCollapse
            storageKey={STORAGE_KEYS.CAPSULE_BOARD_COLLAPSE_STATE}
            activeIds={activeIds}
            setActiveIds={setActiveIds}
            defaultActiveIds={isDefaultFold ? activeIds || ['0'] : ['1']}
            destroyInactivePanel={true}
            className="dbs-home-capsule-collapse"
            title={titleContent}
            isOpacity={isOpacity}
            onStateChange={(ids) => handleStateChange(ids)}
          >
            <CapsuleBar data={metrics} mode={capsuleMode} height={32} directionType={directionType} maxValue={maxValue} valueFormatter={valueFormatter} />
          </CustomCollapse>
          : <>
            {
              title && <div className='dbs-home-metric-card-capsule-title'>{formatMessage({ id: title })}</div>
            }
            <CapsuleBar data={metrics} mode={capsuleMode} height={capsuleHeight} coefficient={coefficient} directionType={directionType} maxValue={maxValue} valueFormatter={valueFormatter} />
          </>
      }
    </div>
  );
}
